/**
 * CommentsDrawer — Kommentare (intern: notes) eines Tages in einem `Drawer`.
 *
 * Gleiche Anker-Logik wie der Detail-Drawer (mobil unten, ab `md` rechts).
 * Zeigt die Kommentarliste (Autor · Zeit · Inhalt), ein Eingabefeld mit
 * Hinzufügen-Button und — an nicht vergangenen Tagen — Löschen für Autor/Admin.
 * Die Anzeige-Texte lauten „Kommentare"/„Comments"; die internen Namen
 * (notes/DayNote) bleiben unverändert.
 */

import { useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Delete from "@mui/icons-material/Delete";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useI18n } from "../../i18n/I18nProvider";
import { formatNoteTime, todayIsoDate } from "./helpers";
import type { DayView, NoteAction } from "./types";

export default function CommentsDrawer({
  open,
  day,
  dayLabel,
  userId,
  isAdmin,
  disabled,
  onNote,
  onClose,
}: {
  open: boolean;
  day: DayView | null;
  dayLabel: string;
  userId: string | undefined;
  isAdmin: boolean;
  disabled: boolean;
  onNote: (action: NoteAction) => void;
  onClose: () => void;
}) {
  const { t, formatDate, formatTime } = useI18n();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("md"));
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const [draft, setDraft] = useState("");

  const isPastDay = day != null && day.date < todayIsoDate();

  function submit() {
    if (!day) return;
    const text = draft.trim();
    if (text === "") return;
    onNote({ kind: "addNote", date: day.date, content: text });
    setDraft("");
  }

  return (
    <Drawer
      anchor={wide ? "right" : "bottom"}
      open={open}
      onClose={onClose}
      transitionDuration={reduceMotion ? 0 : undefined}
      PaperProps={{
        sx: wide
          ? {
              width: 440,
              maxWidth: "100%",
              borderRadius: "22px 0 0 22px",
              backgroundImage: "none",
            }
          : {
              maxWidth: 430,
              mx: "auto",
              borderRadius: "22px 22px 0 0",
              maxHeight: "92vh",
              pb: "env(safe-area-inset-bottom, 0px)",
              backgroundImage: "none",
            },
      }}
    >
      {day != null && (
        <Box
          p={2}
          pb={3}
          component="section"
          aria-label={t("weeklyPlan.comments.sectionAria", { label: dayLabel })}
        >
          <Typography variant="h6" fontWeight={700}>
            {t("weeklyPlan.comments.label")} · {dayLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            {formatDate(day.date)}
          </Typography>

          {day.notes.length === 0 ? (
            <Typography color="text.secondary">
              {t("weeklyPlan.comments.empty")}
            </Typography>
          ) : (
            day.notes.map((note) => {
              const canDelete =
                !isPastDay && (note.authorId === userId || isAdmin);
              return (
                <Box
                  key={note.id}
                  sx={{ bgcolor: "action.hover", borderRadius: 2, p: 1.5, mb: 1 }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {note.authorName} ·{" "}
                      {formatNoteTime(note.createdAt, formatDate, formatTime)}
                    </Typography>
                    {canDelete && (
                      <IconButton
                        size="small"
                        disabled={disabled}
                        aria-label={t("weeklyPlan.comments.deleteAria")}
                        onClick={() =>
                          onNote({ kind: "deleteNote", noteId: note.id })
                        }
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                  <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {note.content}
                  </Typography>
                </Box>
              );
            })
          )}

          {!isPastDay && (
            <Stack direction="row" gap={1} mt={2}>
              <TextField
                fullWidth
                size="small"
                placeholder={t("weeklyPlan.comments.placeholder")}
                value={draft}
                disabled={disabled}
                onChange={(e) => setDraft(e.target.value)}
                inputProps={{
                  maxLength: 1000,
                  "aria-label": t("weeklyPlan.comments.addAria", { label: dayLabel }),
                }}
              />
              <Button
                variant="outlined"
                disabled={disabled || draft.trim() === ""}
                onClick={submit}
                sx={{ minHeight: 44 }}
              >
                {t("weeklyPlan.comments.add")}
              </Button>
            </Stack>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, minHeight: 52 }}
            onClick={onClose}
          >
            {t("weeklyPlan.comments.done")}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}
